/* ------------------------------------------------------------------
 * wishlist.js
 *
 * Wires the heart-icon button (rendered by snippets/wishlist-button.liquid)
 * to Wishlist Plus's (Swym) JS API + dual-pipes wishlist_add /
 * wishlist_remove events to dataLayer + Shopify.analytics.publish,
 * matching the analytics pattern used by mobile-nav, search-modal,
 * home-analytics, etc.
 *
 *   - On page load: reads the user's wishlist and marks any matching
 *     button as aria-pressed="true" so the heart fills in.
 *   - On click: toggles the wishlist state via swat.addToWishList /
 *     removeFromWishList, then updates the button + fires the analytics
 *     event.
 *
 * Gracefully self-noops when Wishlist Plus isn't loaded (app embed
 * disabled in admin) — buttons render but clicks are inert. As soon
 * as the embed is re-enabled, everything starts working without any
 * code change.
 *
 * Click delegation is document-level so dynamically-added buttons
 * (quick view, cart drawer, etc.) work without re-binding.
 * ------------------------------------------------------------------ */
(function () {
  if (!window.SwymCallbacks) window.SwymCallbacks = [];

  /* Swym SDK-only guard (BFCM-PERF 2026-09-23). With Swym's "Show UI" off
   * (window._SwymSDKOnlyMode), Swym's own product-page handler still calls
   * swat.ui.loadSecondaryStyles() and throws, because the UI module is never
   * loaded in that mode. Give it a no-op. The empty button bar Swym injects
   * afterwards is already hidden by assets/custom.css ("WISHLIST PLUS (SWYM)
   * — suppress their auto-injected UI"). unshift: run before other callbacks. */
  window.SwymCallbacks.unshift(function (swat) {
    if (swat && !swat.ui) swat.ui = { loadSecondaryStyles: function () {} };
  });

  var swatRef = null;
  var wishlistedIds = new Set();
  var totalWishlistCount = 0;  // total ITEMS (variants), not unique products
  // Lifetime first-add dismissal. Persisted in localStorage so the
  // drawer never auto-opens again after the user has seen it once.
  // Subsequent adds still update the badge + heart fill, just no
  // intrusive drawer slide-in. Set when the drawer opens after an
  // add (here), AND when an add comes from the cart context (in the
  // quick-cart inline script — the user clearly knows about wishlist
  // if they're using Save for Later).
  // Bumped to v2 so users who had the legacy key set during the
  // cart-undo-Save-to-Wishlist regression window (which poisoned the
  // flag without ever opening the modal) get the first-add nudge
  // ONE more time on their next heart-click. Cheap, recovers users
  // who were stranded by the bug, no downside for healthy sessions
  // (they see the modal at most once more in their lifetime).
  var FIRST_ADD_DISMISSED_KEY = 'rastah_wishlist_first_add_dismissed_v2';
  function hasDismissedFirstAdd() {
    try { return window.localStorage.getItem(FIRST_ADD_DISMISSED_KEY) === '1'; }
    catch (e) { return false; }
  }
  function markFirstAddDismissed() {
    try { window.localStorage.setItem(FIRST_ADD_DISMISSED_KEY, '1'); }
    catch (e) {}
  }

  /* ---- localStorage mirror -------------------------------------- *
   * Swym's `fetchLists` JS API needs auth params we can't cleanly
   * obtain from the client (it lives behind their widget plumbing).
   * As a workaround, we mirror every add/remove into localStorage so
   * the drawer + account-page UI can render wishlist contents without
   * needing fetchLists.
   *
   * Source of truth is still Swym server-side (their dedup, their
   * Klaviyo bridge, their multi-device merge). localStorage is a
   * LOCAL cache for UI rendering. Reconciled by `fetchWishlistCount`
   * — if server count > local count, we show a "more items on other
   * devices" hint in the drawer.
   * ---------------------------------------------------------------- */
  var LS_KEY = 'rastah_wishlist_items_v1';

  function readLocalWishlist() {
    try {
      var raw = window.localStorage.getItem(LS_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) { return []; }
  }

  function writeLocalWishlist(items) {
    try {
      window.localStorage.setItem(LS_KEY, JSON.stringify(items || []));
    } catch (e) { /* quota / private mode — silent */ }
  }

  function addToLocalWishlist(item) {
    var items = readLocalWishlist();
    var existing = items.findIndex(function (i) { return i.empi === item.empi && i.epi === item.epi; });
    if (existing >= 0) return items;
    items.unshift(Object.assign({ _added_at: Date.now() }, item));
    writeLocalWishlist(items);
    /* notify other components (drawer hydrator etc.) */
    document.dispatchEvent(new CustomEvent('rastah:wishlist:local-updated', { detail: { count: items.length } }));
    return items;
  }

  function removeFromLocalWishlist(pid, vid) {
    var items = readLocalWishlist().filter(function (i) { return !(i.empi === pid && i.epi === vid); });
    writeLocalWishlist(items);
    document.dispatchEvent(new CustomEvent('rastah:wishlist:local-updated', { detail: { count: items.length } }));
    return items;
  }

  /* Public read for other scripts (quick-account.js, wishlist-account.js) */
  window.RastahWishlist = window.RastahWishlist || {};
  window.RastahWishlist.getLocalItems = readLocalWishlist;
  window.RastahWishlist.LS_KEY = LS_KEY;

  /* ---- Analytics ------------------------------------------------- */
  function track(event, props) {
    var tmplMatch = String(document.body.className || '').match(/template-(\S+)/);
    var payload = Object.assign({
      event: event,
      ts: Date.now(),
      page_path: window.location.pathname + window.location.search,
      page_template: tmplMatch ? tmplMatch[1] : 'unknown'
    }, props || {});
    (window.dataLayer = window.dataLayer || []).push(payload);
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(event, payload);
      }
    } catch (e) {}
  }

  /* ---- Button state ---------------------------------------------- */
  function setBtnState(btn, active) {
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    /* BFCM "Save for launch" keeps its own name ("Save for launch");
     * aria-pressed carries the state (snippets/wishlist-button.liquid). */
    if (btn.hasAttribute('data-bfcm-save')) return;
    btn.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
  }

  function refreshAllButtons() {
    var buttons = document.querySelectorAll('[data-wishlist-btn]');
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      var pid = parseInt(btn.getAttribute('data-wishlist-product-id'), 10);
      setBtnState(btn, wishlistedIds.has(pid));
    }
  }

  /* Update the count badge in the header (and any other surface
   * with [data-wishlist-count]). Hidden when count is 0. */
  function updateBadge(count) {
    totalWishlistCount = count;
    var badges = document.querySelectorAll('[data-wishlist-count]');
    for (var i = 0; i < badges.length; i++) {
      var b = badges[i];
      b.textContent = count > 99 ? '99+' : String(count);
      if (count > 0) b.removeAttribute('hidden');
      else b.setAttribute('hidden', '');
    }
  }

  /* Hydrate page-load state:
   *   1. Badge count → from Swym server via fetchWishlistCount
   *      (single source of truth; reconciles across devices)
   *   2. Heart fill state → from localStorage mirror
   *      (per-device cache; covers the common case where users
   *      wishlist + browse on the same device)
   *
   * If a user wishlists an item on device A, then on device B the
   * hearts won't be filled (no LS mirror there), but the badge will
   * still show the correct server-side count. */
  function hydrateHeartsFromLocal() {
    var items = readLocalWishlist();
    wishlistedIds = new Set(items.map(function (i) { return parseInt(i.empi, 10); }));
    refreshAllButtons();
  }

  function syncWishlistState() {
    /* Badge always reflects the LOCAL wishlist count — i.e. what
     * the user can actually see in the drawer on THIS device.
     *
     * We do NOT pull from Swym's server here. Why:
     *   1. The drawer can only render items in the localStorage
     *      mirror (Swym's `fetchLists` requires auth params we
     *      can't extract from the storefront). So if the badge
     *      showed server's count, it could be ≥ what the drawer
     *      displays — "your wishlist has 2 items" vs the user
     *      sees an empty drawer. Confusing.
     *   2. Server propagation lag on remove caused the previous
     *      "stale 2 stuck on empty wishlist" bug.
     *
     * Trade-off: cross-device sync of the BADGE doesn't work
     * (a fresh device shows 0 even if the user wishlisted on
     * another device). That matches the drawer's behavior, so
     * the two surfaces never disagree. Acceptable until/unless
     * we adopt a server-side wishlist API we can fully read. */
    hydrateHeartsFromLocal();
    updateBadge(readLocalWishlist().length);
  }

  /* ---- Swym payload builder -------------------------------------- */
  /* Find the currently-selected variant ID. The PDP product form has a
   * <select name="id"> that Stiletto keeps in sync with the chosen
   * size buttons. Falls back to the variant captured at render time
   * if no form is in scope (e.g. collection-card heart, where the
   * variant is whatever was first-available at page render). */
  function currentVariantId(btn) {
    var fallback = parseInt(btn.getAttribute('data-wishlist-variant-id'), 10) || 0;
    var form = btn.closest('form');
    if (!form) return fallback;
    var idInput = form.querySelector('[name="id"]');
    if (!idInput) return fallback;
    var v = parseInt(idInput.value, 10);
    return v || fallback;
  }

  /* Look up the option1 (size) string for a variant ID using the
   * inline JSON map dropped on the button at render time. */
  function variantSize(btn, variantId) {
    /* Fast path — if the button was just rendered with the current
     * variant + option1, use the cached attr. */
    var fallbackId = parseInt(btn.getAttribute('data-wishlist-variant-id'), 10);
    if (variantId === fallbackId) {
      var fallbackOpt = btn.getAttribute('data-wishlist-variant-option1');
      if (fallbackOpt) return fallbackOpt;
    }
    var raw = btn.getAttribute('data-wishlist-variants');
    if (!raw) return '';
    try {
      var list = JSON.parse(raw);
      for (var i = 0; i < list.length; i++) {
        if (parseInt(list[i].id, 10) === variantId) {
          return list[i].option1 || list[i].title || '';
        }
      }
    } catch (e) {}
    return '';
  }

  /* Swym-safe payload — these are the only fields Swym's `recordWishlistAction`
   * schema accepts. Adding an unknown key (e.g. `va`) causes a 400
   * "disallowed-key" response and the add silently fails. */
  function productPayload(btn) {
    var vid = currentVariantId(btn);
    return {
      empi: parseInt(btn.getAttribute('data-wishlist-product-id'), 10),
      epi: vid,
      du: btn.getAttribute('data-wishlist-product-url'),
      iu: btn.getAttribute('data-wishlist-product-image'),
      dt: btn.getAttribute('data-wishlist-product-title'),
      pr: (parseInt(btn.getAttribute('data-wishlist-product-price'), 10) || 0) / 100
    };
  }

  /* Extra fields we attach ONLY to the localStorage mirror (never
   * sent to Swym). Used by the drawer to render size pills, etc.
   * Persists locally on this device only — cross-device users won't
   * see size info on items wishlisted from another device. That's
   * an acceptable trade-off vs. Swym's strict server schema.
   *
   * `unrl` flags the entry as a pre-release / unreleased drop —
   * Liquid sets `data-wishlist-unreleased="true"` on the heart for
   * products that have no purchasable variants AND are in a
   * collection wired with the Klaviyo waitlist metafield (or are
   * tagged `unreleased`/`coming-soon`/`pre-release`). Every wishlist
   * tile surface (drawer, dashboard, Enclave sheet) reads this and
   * disables Move-to-Bag so the user can't try to add a product
   * that isn't purchasable yet. */
  function localExtras(btn) {
    return {
      va: variantSize(btn, currentVariantId(btn)),
      unrl: btn.getAttribute('data-wishlist-unreleased') === 'true' ? 1 : 0
    };
  }

  function analyticsPayload(btn, extra) {
    var base = {
      product_id: btn.getAttribute('data-wishlist-product-id'),
      product_handle: btn.getAttribute('data-wishlist-product-handle'),
      variant_id: btn.getAttribute('data-wishlist-variant-id'),
      product_title: btn.getAttribute('data-wishlist-product-title') || '',
      variant_title: btn.getAttribute('data-wishlist-variant-title') || '',
      source: btn.getAttribute('data-wishlist-source') || 'unknown',
      is_anonymous: !window.customer || !window.customer.id,
      // Cohort field: separates wishlist saves on pre-release drops
      // (high commercial-intent signal — user is committing to a
      // product before it's purchasable) from regular wishlists.
      is_unreleased: btn.getAttribute('data-wishlist-unreleased') === 'true'
    };
    return Object.assign(base, extra || {});
  }

  /* Swym ops that fired before Swym finished loading get queued
   * here and replayed once `swatRef` resolves. Each entry is the
   * raw payload + intended action ('add' / 'remove'). */
  var pendingSwymOps = [];

  /* ---- Click handling -------------------------------------------- *
   *
   * Optimistic-UI model: every visual state change (heart fill,
   * badge count, localStorage mirror) happens IMMEDIATELY on click,
   * regardless of whether Swym (the wishlist backend) is ready. The
   * user sees instant feedback every time.
   *
   * If Swym isn't loaded yet, the Swym op is queued and fired when
   * the SwymCallbacks queue resolves. If Swym fails server-side, we
   * keep the optimistic local state — the user has a working
   * wishlist regardless. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-wishlist-btn]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    if (btn.classList.contains('wishlist-btn--loading')) return;

    var pid = parseInt(btn.getAttribute('data-wishlist-product-id'), 10);
    var payload = productPayload(btn);
    var isWishlisted = btn.getAttribute('aria-pressed') === 'true';

    /* ---- Optimistic UI updates ---- *
     * Flip everything BEFORE we touch the network. */
    btn.classList.add('wishlist-btn--loading');
    if (isWishlisted) {
      wishlistedIds.delete(pid);
      updateBadge(Math.max(0, totalWishlistCount - 1));
      removeFromLocalWishlist(pid, payload.epi);
    } else {
      wishlistedIds.add(pid);
      updateBadge(totalWishlistCount + 1);
      btn.classList.add('wishlist-btn--just-added');
      setTimeout(function () { btn.classList.remove('wishlist-btn--just-added'); }, 350);
      addToLocalWishlist(Object.assign({}, payload, localExtras(btn)));
    }
    setBtnState(btn, !isWishlisted);

    track(isWishlisted ? 'wishlist_remove' : 'wishlist_add', analyticsPayload(btn));

    /* CTA-mode tap — fires on every tap of a `wishlist-btn--cta`
     * (the promoted heart that replaces the buy button on unreleased
     * products, plus its sticky-bar twin). Distinct from
     * `wishlist_add` so the merchant can isolate conversion behavior
     * on gated drops vs regular heart taps elsewhere. Carries the
     * `surface` field so sticky vs in-flow taps can be cohorted. */
    if (btn.classList.contains('wishlist-btn--cta')) {
      var surface = btn.closest('[data-mobile-sticky-atc]') ? 'sticky' : 'in_flow';
      track('wishlist_cta_tap', analyticsPayload(btn, {
        surface: surface,
        action: isWishlisted ? 'remove' : 'add'
      }));
    }

    /* Sticky fade-out on save. Once the user commits the gated
     * product to wishlist via the promoted CTA, the sticky has done
     * its job — fade it out so it doesn't keep following the scroll
     * (user feedback: "should go away and not be sticky beyond the
     * first viewport"). A `data-rastah-sticky-dismissed` session-
     * scoped attribute on <body> persists the dismissal across
     * SPA-style nav within the page (back/forward stays clean). */
    if (!isWishlisted && btn.classList.contains('wishlist-btn--cta')) {
      var stickyHost = document.querySelector('[data-mobile-sticky-atc][data-mobile-sticky-atc-mode="unreleased"]');
      if (stickyHost) {
        stickyHost.classList.add('mobile-sticky-atc--dismissed');
        document.body.setAttribute('data-rastah-sticky-dismissed', '1');
        // Allow the 320ms fade to play, then hide the element so it
        // can't intercept taps even at opacity 0.
        setTimeout(function () { stickyHost.setAttribute('hidden', ''); }, 360);
      }
    }

    /* First-add nudge — fires ONCE per user, lifetime (not per
     * session). After the drawer/sheet opens the first time, the
     * dismissal flag is persisted in localStorage; subsequent adds
     * still update the badge + heart fill but don't intrude on
     * browsing. Rationale: the drawer auto-open is a "show the user
     * where the wishlist lives" cue — after that, the heart icon
     * + header badge are enough.
     *
     * LOGGED-IN customers see the dark "Enclave wishlist sheet"
     * (bottom-sheet overlay rendered in layout/theme.liquid) — same
     * card styling as the /account dashboard's wishlist tab. Drops
     * the legacy quick-account drawer for this flow because that
     * drawer's value-add (sign-in CTA, dashboard preview) is
     * already redundant for logged-in users.
     *
     * LOGGED-OUT customers continue to see the quick-account drawer
     * — the sign-in CTA is the reason to open it for them.
     *
     * Hardening (post-regression): only set the dismissal flag if
     * the modal/drawer ACTUALLY opens. The previous version flagged
     * pre-emptively, which meant any path that silently no-op'd
     * (sheet element missing, RastahQuickAccount not yet hydrated,
     * an exception during open) burned the user's one-shot nudge.
     * `firstAddInFlight` guards against rapid double-clicks racing
     * two concurrent opens. */
    var isLoggedIn = !!(window.customer && window.customer.id);
    /* data-wishlist-no-nudge: BFCM "Save for launch" (snippets/wishlist-button.liquid,
     * mode 'bfcm_save') never opens the drawer; assets/bfcm-gate.js shows its
     * own toast on the device's first save only. */
    if (!isWishlisted && !btn.hasAttribute('data-wishlist-no-nudge') && !hasDismissedFirstAdd() && !window.__rastahFirstAddInFlight) {
      window.__rastahFirstAddInFlight = true;
      setTimeout(function () {
        var opened = false;
        try {
          if (isLoggedIn && window.RastahWishlistSheet && typeof window.RastahWishlistSheet.open === 'function') {
            window.RastahWishlistSheet.open('first_wishlist_add');
            opened = true;
          } else if (window.RastahQuickAccount && typeof window.RastahQuickAccount.open === 'function') {
            window.RastahQuickAccount.setTab('wishlist');
            window.RastahQuickAccount.open('first_wishlist_add');
            opened = true;
          }
        } catch (err) {
          console.warn('[wishlist] first-add modal open failed:', err);
        }
        if (opened) markFirstAddDismissed();
        window.__rastahFirstAddInFlight = false;
      }, 250);
    }

    /* ---- Background Swym sync ---- *
     * If Swym is ready right now, fire the op. Otherwise queue it
     * and replay from the SwymCallbacks handler below. */
    function clearLoading() {
      btn.classList.remove('wishlist-btn--loading');
    }
    function onSuccess() { clearLoading(); }
    function onError(err) {
      clearLoading();
      console.warn('[wishlist] Swym op failed:', err);
    }

    var swat = swatRef || window._swat;
    if (swat && typeof swat.addToWishList === 'function') {
      var op = isWishlisted
        ? swat.removeFromWishList.bind(swat)
        : swat.addToWishList.bind(swat);
      op(payload, onSuccess, onError);
    } else {
      /* Swym not ready — keep the loading spinner on briefly so the
       * user knows we're still working, then clear after a short
       * timeout. The op gets replayed when Swym callbacks fire. */
      pendingSwymOps.push({
        action: isWishlisted ? 'remove' : 'add',
        payload: payload,
        btn: btn
      });
      setTimeout(clearLoading, 600);
    }
  });

  /* ---- Drain pre-handler queue ---------------------------------- *
   * theme.liquid's inline capture-phase click handler catches any
   * heart taps that fire BEFORE this script runs (defer load). It
   * stashes the button references in window.__rastahWishlistQueue
   * and applies a loading state. Replay them now by re-firing
   * .click() on each — the regular delegated listener above picks
   * them up with optimistic UI. */
  window.__rastahWishlistReady = true;
  if (Array.isArray(window.__rastahWishlistQueue) && window.__rastahWishlistQueue.length) {
    var pendingClicks = window.__rastahWishlistQueue.slice();
    window.__rastahWishlistQueue = [];
    pendingClicks.forEach(function (btn) {
      if (!btn || !btn.isConnected) return;
      btn.classList.remove('wishlist-btn--loading');
      try { btn.click(); } catch (e) {}
    });
  }

  /* ---- Boot ------------------------------------------------------ */
  /* Eager local hydration — DON'T wait for Swym. The localStorage
   * mirror is the source of truth for HEART STATE; Swym is only
   * involved for cross-device badge reconciliation. Calling
   * hydrateHeartsFromLocal() immediately means the CTA on a gated
   * PDP shows "Saved" instead of "SAVE TO WISHLIST" the moment the
   * page loads, instead of flickering through the default state
   * until Swym's callback fires (which can be 1-3 s on cold loads
   * or never if the embed is blocked).
   *
   * We still re-run syncWishlistState inside the SwymCallbacks
   * below for badge reconciliation — the hydrateHeartsFromLocal
   * call there is idempotent. */
  hydrateHeartsFromLocal();
  updateBadge(readLocalWishlist().length);

  /* Hearts added after boot (an in-place collection tab swap, infinite
   * scroll, quick view) render unpressed from Liquid. Re-apply the
   * current state to them, once per batch of DOM changes; otherwise a
   * saved piece reads "Save" after switching tabs and back, and a tap
   * on it would add it a second time. */
  (function () {
    var queued = false;
    function queue() {
      if (queued) return;
      queued = true;
      setTimeout(function () { queued = false; refreshAllButtons(); }, 0);
    }
    document.addEventListener('shopify:section:load', queue);
    if (!window.MutationObserver) return;
    new MutationObserver(function (records) {
      if (queued) return;
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType === 1 && (n.hasAttribute('data-wishlist-btn') || n.querySelector('[data-wishlist-btn]'))) { queue(); return; }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  })();

  window.SwymCallbacks.push(function (swat) {
    swatRef = swat;
    /* Initial page-load reconciliation — only place the BADGE pulls
     * from server. After this, local mutations drive the badge
     * directly via the rastah:wishlist:local-updated listener. */
    syncWishlistState(swat);

    /* Drain any Swym ops that were queued while Swym was still
     * loading — replay in FIFO order so add-then-remove sequences
     * resolve on the server in the order the user performed them. */
    while (pendingSwymOps.length) {
      var queued = pendingSwymOps.shift();
      var fn = queued.action === 'add' ? swat.addToWishList : swat.removeFromWishList;
      if (typeof fn !== 'function') continue;
      try {
        fn.call(swat, queued.payload, function noopSuccess() {}, function noopError() {});
      } catch (e) { /* keep going on partial failure */ }
    }
  });

  /* Re-sync heart fill state + badge when other surfaces (drawer,
   * account page) mutate the localStorage mirror. This keeps the PDP
   * heart in sync when user removes from the drawer (heart should
   * un-fill) or adds via another surface. */
  document.addEventListener('rastah:wishlist:local-updated', function (e) {
    /* Source of truth for the BADGE after a local mutation is the
     * local count, NOT Swym's server count.
     *
     * Why: Swym's server has propagation lag — when the user clicks
     * × to remove an item, our localStorage updates instantly but
     * Swym's `fetchWishlistCount` may still report the old (pre-
     * remove) count for a moment. If we sync from server right after
     * the local update, the badge briefly flicks back to the old
     * count and STICKS there if the sync wins the race. That's the
     * "empty wishlist still shows 2" bug.
     *
     * We don't sync from server here. The initial page-load sync
     * (in the SwymCallbacks boot below) already reconciles cross-
     * device state; subsequent mutations on this device are the
     * user's just-performed action, which local already reflects. */
    hydrateHeartsFromLocal();
    var localCount = (e && e.detail && typeof e.detail.count === 'number')
      ? e.detail.count
      : readLocalWishlist().length;
    updateBadge(localCount);
  });

  /* ---- Enclave wishlist sheet (logged-in overlay) ---------------- *
   *
   * Replaces the legacy quick-account drawer for the first-add nudge
   * when the customer is signed in. Renders product cards in the
   * same dark aesthetic as /account?tab=wishlist (reuses
   * .account__wishlist--enclave styling), giving the user a
   * consistent visual language whether they land on the dashboard
   * or trigger the sheet from a PDP/PLP.
   *
   * Sheet markup lives in layout/theme.liquid behind {% if customer %}
   * — so it's only present on the page when there's a customer to
   * see it. JS self-noops when the sheet element isn't found
   * (logged-out flows safely skip this block).
   * --------------------------------------------------------------- */
  var sheet = document.querySelector('[data-enclave-wishlist-sheet]');
  if (sheet) {
    var sheetGrid = sheet.querySelector('[data-enclave-wishlist-sheet-grid]');
    var sheetSavedScrollY = 0;
    var sheetLastFocused = null;

    function renderSheet() {
      if (!sheetGrid) return;
      var items = readLocalWishlist();
      if (!items.length) {
        sheetGrid.innerHTML = '<p class="account__wishlist-empty">No items saved yet.</p>';
        return;
      }
      function esc(s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;'); }
      function fmtPrice(p) {
        if (window.Shopify && Shopify.formatMoney) return Shopify.formatMoney(Math.round((p || 0) * 100));
        return (p || 0).toFixed(2);
      }
      // Tier multiplier sourced from the dashboard section root (set
      // by Liquid). Falls back to 1 (Bronze baseline) when the
      // dashboard isn't on the current page.
      var sheetRoot = document.querySelector('[data-enclave-dashboard]');
      var sheetMultiplier = sheetRoot ? parseFloat(sheetRoot.getAttribute('data-tier-multiplier')) || 1 : 1;
      sheetGrid.innerHTML = items.map(function (it) {
        var url = it.du || '#';
        var img = it.iu || '';
        var title = String(it.dt || 'Untitled');
        var price = it.pr;
        var size = String(it.va || '').trim();
        var earnPts = price != null ? Math.floor((price || 0) * sheetMultiplier) : null;
        // `unrl` flag — pre-release / unreleased product.
        // Sheet renders with a locked state (no Move-to-Bag, lock chip).
        var isUnreleased = it.unrl === 1 || it.unrl === true;

        var middleHTML = isUnreleased
          ? '<span class="account__wishlist-card-unreleased">' +
              '<svg width="11" height="11" viewBox="0 0 11 11" aria-hidden="true" style="vertical-align:-1px;margin-right:4px"><path d="M3 5V3.5a2.5 2.5 0 0 1 5 0V5M2 5h7v5H2z" fill="none" stroke="currentColor" stroke-width="1"/></svg>' +
              'Releases soon' +
            '</span>' +
            (earnPts != null && earnPts > 0
              ? '<p class="account__wishlist-card-earn">Earn ' + earnPts.toLocaleString('en-US') + ' pts on release</p>'
              : '')
          : (size ? '<p class="quick-cart__item-variant fs-body-75">' + esc(size) + '</p>' : '') +
            (earnPts != null && earnPts > 0
              ? '<p class="account__wishlist-card-earn">Earn ' + earnPts.toLocaleString('en-US') + ' pts</p>'
              : '');

        var bottomHTML = isUnreleased
          ? '<span class="account__wishlist-card-locked-msg">We’ll notify you when live</span>'
          : '<button class="account__wishlist-card-move" type="button" data-sheet-card-move>Move to bag</button>';

        /* Same cart line-item shape as the /account dashboard
         * wishlist tab — markup parity = consistent visual
         * regardless of trigger surface. */
        return '' +
          '<div class="quick-cart__item account__wishlist-card account__wishlist-card--enclave' + (isUnreleased ? ' account__wishlist-card--unreleased' : '') + '" data-pid="' + esc(it.empi) + '" data-vid="' + esc(it.epi) + '"' + (isUnreleased ? ' data-unreleased="true"' : '') + '>' +
            '<div class="quick-cart__item-image">' +
              '<a href="' + esc(url) + '" class="quick-cart__item-link">' +
                (img ? '<img src="' + esc(img) + '" alt="' + esc(title) + '" loading="lazy">' : '') +
              '</a>' +
            '</div>' +
            '<div class="quick-cart__item-details">' +
              '<div class="quick-cart__item-top">' +
                '<div class="quick-cart__item-title-row">' +
                  '<h4 class="quick-cart__item-title fs-product-card-title">' +
                    '<a href="' + esc(url) + '">' + esc(title) + '</a>' +
                  '</h4>' +
                  '<div class="quick-cart__item-price-wrapper fs-body-75">' +
                    (price != null ? '<span class="quick-cart__item-price">' + fmtPrice(price) + '</span>' : '') +
                  '</div>' +
                '</div>' +
              '</div>' +
              '<div class="quick-cart__item-middle">' + middleHTML + '</div>' +
              '<div class="quick-cart__item-bottom">' + bottomHTML + '</div>' +
            '</div>' +
          '</div>';
      }).join('');
    }

    function openSheet(source) {
      renderSheet();
      sheetLastFocused = document.activeElement;
      sheet.removeAttribute('hidden');
      sheetSavedScrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(function () {
        sheet.setAttribute('data-open', 'true');
      });
      var closeBtn = sheet.querySelector('.enclave-wishlist-sheet__close');
      if (closeBtn) closeBtn.focus();
      // Mirror the analytics pattern used elsewhere (dataLayer + Shopify.analytics).
      try {
        (window.dataLayer = window.dataLayer || []).push({
          event: 'wishlist_sheet_open',
          source: source || 'unknown',
          item_count: readLocalWishlist().length,
          ts: Date.now()
        });
      } catch (_) {}
    }

    function closeSheet() {
      sheet.removeAttribute('data-open');
      document.body.style.overflow = '';
      setTimeout(function () {
        sheet.setAttribute('hidden', '');
        window.scrollTo(0, sheetSavedScrollY);
        if (sheetLastFocused && typeof sheetLastFocused.focus === 'function') {
          sheetLastFocused.focus();
        }
      }, 200);
    }

    sheet.addEventListener('click', function (e) {
      if (e.target.closest('[data-enclave-wishlist-sheet-close]')) {
        e.preventDefault();
        closeSheet();
        return;
      }
      // Move-to-Bag on a card inside the sheet
      var moveBtn = e.target.closest('[data-sheet-card-move]');
      if (moveBtn) {
        e.preventDefault();
        var card = moveBtn.closest('.account__wishlist-card');
        if (!card) return;
        // Defense in depth: unreleased tiles don't render this
        // button, but if the DOM gets hot-edited bail before
        // hitting /cart/add.js for a non-purchasable product.
        if (card.getAttribute('data-unreleased') === 'true') return;
        var vid = parseInt(card.getAttribute('data-vid'), 10);
        var pid = parseInt(card.getAttribute('data-pid'), 10);
        if (!vid) return;
        var originalLabel = moveBtn.textContent;
        moveBtn.disabled = true;
        moveBtn.textContent = 'Adding…';
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: vid, quantity: 1 })
        })
        .then(function (r) { return r.json().then(function (b) { return { ok: r.ok, body: b }; }); })
        .then(function (res) {
          if (!res.ok) {
            moveBtn.disabled = false;
            moveBtn.textContent = (res.body && res.body.description) ? 'Sold out' : originalLabel;
            setTimeout(function () { moveBtn.disabled = false; moveBtn.textContent = originalLabel; }, 1800);
            return;
          }
          moveBtn.textContent = 'Added ✓';
          // Remove from local wishlist + refresh sheet
          var items = readLocalWishlist();
          var next = items.filter(function (it) { return !(it.empi === pid && it.epi === vid); });
          writeLocalWishlist(next);
          updateBadge(next.length);
          document.dispatchEvent(new CustomEvent('rastah:wishlist:changed', {
            detail: { count: next.length, source: 'enclave_sheet' }
          }));
          // Close the wishlist sheet immediately (UI), then reconcile
          // the free gift BEFORE refreshing. This raw /cart/add.js
          // bypasses Stiletto's cart pipeline, so theme.min.js's gift
          // sync never fires for a wishlist→bag move — add any newly
          // qualifying gift first so the count + re-rendered drawer
          // include it. Guarded + non-throwing.
          closeSheet();
          (window.rastahSyncFreeGifts ? window.rastahSyncFreeGifts() : Promise.resolve()).then(function () {
            // Refresh header cart count (now reflects any gift)
            fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
              .then(function (rr) { return rr.json(); })
              .then(function (cart) {
                document.querySelectorAll('[data-js-cart-count]').forEach(function (el) {
                  el.textContent = String(cart.item_count || 0);
                });
              })
              .catch(function () {});
            // Refresh + open the cart drawer. Same chained pattern as the
            // logged-out quick-account flow: fetch the new quick-cart
            // HTML, swap it in, then click the cart icon to open
            // Stiletto's drawer.
            fetch('/cart?section_id=quick-cart', { credentials: 'same-origin' })
              .then(function (r) { return r.text(); })
              .then(function (html) {
                var holder = document.createElement('div');
                holder.innerHTML = html;
                var freshContainer = holder.querySelector('.quick-cart__container');
                var liveContainer = document.querySelector('.quick-cart .quick-cart__container');
                if (freshContainer && liveContainer) {
                  liveContainer.innerHTML = freshContainer.innerHTML;
                }
                // Re-run the free-shipping / gift progress bar on the
                // swapped-in drawer (the manual swap injects an unfilled
                // bar; Stiletto's own refresh is unreachable from here).
                var fsbBar = document.querySelector('.quick-cart [data-free-shipping-bar]');
                if (fsbBar && window.rastahRefreshFreeShippingBar) window.rastahRefreshFreeShippingBar(fsbBar);
                setTimeout(function () {
                  var cartIcon = document.querySelector('[data-js-cart-icon]');
                  if (cartIcon) cartIcon.click();
                }, 220);
              })
              .catch(function () {
                setTimeout(function () {
                  var cartIcon = document.querySelector('[data-js-cart-icon]');
                  if (cartIcon) cartIcon.click();
                }, 220);
              });
          });
        });
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && sheet.getAttribute('data-open') === 'true') {
        closeSheet();
      }
    });

    // Re-render if the wishlist changes while the sheet is open
    document.addEventListener('rastah:wishlist:changed', function () {
      if (sheet.getAttribute('data-open') === 'true') renderSheet();
    });

    window.RastahWishlistSheet = { open: openSheet, close: closeSheet };
  }
})();
